import React from 'react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (pageNumber: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({ currentPage, totalPages, onPageChange }) => {
  const maxPageButtons = 4;
  const pages: (number | string)[] = [];

  let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
  let endPage = startPage + maxPageButtons - 1;

  if (endPage > totalPages) {
    endPage = totalPages;
    startPage = Math.max(1, endPage - maxPageButtons + 1);
  }

  if (totalPages > maxPageButtons && startPage > 1) {
    pages.push(1);
    if (startPage > 2) {
      pages.push('ellipsis-start');
    }
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  if (totalPages > maxPageButtons && endPage < totalPages - 1) {
    if (endPage < totalPages - 1) {
      pages.push('ellipsis-end');
    }
    pages.push(totalPages);
  }

  return (
    <div className="flex justify-center items-center gap-1">
      <button
        className={`px-3 py-2 rounded-lg transition-all duration-200 ${
          currentPage === 1 
            ? 'text-gray-300 cursor-not-allowed' 
            : 'text-cyan-700 hover:bg-cyan-50 hover:text-cyan-800'
        }`}
        onClick={() => currentPage > 1 && onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        &laquo;
      </button>
      {pages.map((page, index) => {
        if (typeof page === 'string') {
          return (
            <span key={`ellipsis-${index}`} className="px-2 text-gray-400">
              ...
            </span>
          );
        }
        const isActive = currentPage === page;
        return (
          <button
            key={page}
            className={`min-w-[40px] px-3 py-2 rounded-lg transition-all duration-200 ${
              isActive
                ? 'bg-gradient-to-br from-cyan-600 to-cyan-700 text-white shadow-md font-semibold'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
            onClick={() => onPageChange(page)}
          >
            {page}
          </button>
        );
      })}
      <button
        className={`px-3 py-2 rounded-lg transition-all duration-200 ${
          currentPage === totalPages 
            ? 'text-gray-300 cursor-not-allowed' 
            : 'text-cyan-700 hover:bg-cyan-50 hover:text-cyan-800'
        }`}
        onClick={() => currentPage < totalPages && onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        &raquo;
      </button>
    </div>
  );
};

export default Pagination;
